import {
  AddressDto,
  EnterpriseDto,
} from '@enterprise/models/dto/enterprise-create.dto';
import { Divider, Stack } from '@mui/material';
import Jtab, { JTabItem } from '@atoms/JTab';
import { useNavigate, useParams } from 'react-router-dom';

import EnterpriseCredits from '../EnterpriseCredits';
import EnterpriseDetails from '@enterprise/components/EnterpriseDetails';
import { JButton } from '@atoms/JButton';
import { Loading } from '@atoms/Loading';
import Paths from '@/routes/paths';
import { TopBar } from '@/layouts/TopBar';
import useCreateEnterprise from '@enterprise/hooks/useCreateEnterprise';
import { useEditEnterpriseRequest } from '@enterprise/mutations/enterpriseMutations';
import { useEffect } from 'react';
import { useGetEnterpriseById } from '@enterprise/queries/useGetEnterprises';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const EnterpriseEditForm = () => {
  const { showSnackbar } = useJSnackbar();
  const queryClient = useQueryClient();
  const {
    mutate: editEnterprise,
    isLoading: editEnterpriseLoading,
    isSuccess: editEnterpriseIsSuccess,
    isError: editEnterpriseIsError,
  } = useEditEnterpriseRequest();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isSuccess, isLoading } = useGetEnterpriseById(id, !!id);
  const { setPayload, setSectors, isDirty, payload } = useCreateEnterprise();

  const dtoData = data?.data;
  const addressDtoData = data?.data.address;
  const consumptionAllowedActions = dtoData?.consumptionAllowedActions;
  const itemsList: JTabItem[] = [
    {
      title: 'GENERAL_INFORMATIONS',
      component: <EnterpriseDetails />,
    },
    {
      title: 'CREDITS',
      component: (
        <EnterpriseCredits
          id={id}
          consumptionAllowedActions={consumptionAllowedActions}
        />
      ),
    },
  ];

  const fillForm = async () => {
    if (!dtoData || !addressDtoData) return;
    // fill payload
    const address: AddressDto = {
      countryName: addressDtoData.country.name,
      countryId: addressDtoData.country.id,

      cityName: addressDtoData.city?.name
        ? `${addressDtoData.city?.name} / ${addressDtoData.city?.zipCode}`
        : addressDtoData.cityName,
      additionalStreet: addressDtoData.additionalStreet ?? '',
      street: addressDtoData.street,
      cityId: addressDtoData?.city?.id ?? '',
    };

    const enterprise: EnterpriseDto = {
      name: dtoData.name,
      description: dtoData.description,
      email: dtoData.email,
      siren: dtoData.siren,
      activityAreaIds: dtoData.activityAreas.map((item) => item.romeDomain.id),
    };

    setPayload({
      addressDto: address,
      enterpriseDto: enterprise,
      logoUrl: dtoData.logoUrl ?? undefined,
      videoUrl: dtoData.videoUrl ?? undefined,
      thumbnailUrl: dtoData.thumbnailUrl ?? undefined,
      id: dtoData.id,
    });
  };
  useEffect(() => {
    if (!isSuccess || isLoading) return;
    fillForm();
    setSectors(
      dtoData?.activityAreas
        .filter((item) => item !== undefined)
        .map((item) => ({
          label: item.romeDomain.name,
          value: item.romeDomain.id,
        })),
    );
  }, [isSuccess, dtoData, setSectors]);

  useEffect(() => {
    if (!editEnterpriseIsSuccess) return;
    queryClient.invalidateQueries(['enterprises']);
    navigate(Paths.EnterprisePage);
    showSnackbar(t('ENTERPRISE_EDIT_SUCCESS'), 'success');
  }, [editEnterpriseIsSuccess, navigate, queryClient, id, showSnackbar, t]);

  useEffect(() => {
    if (editEnterpriseIsError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [editEnterpriseIsError]);

  if (isLoading || editEnterpriseLoading) return <Loading />;

  return (
    <>
      <TopBar title={t('EDIT_ENTERPRISE')}>
        <Stack direction={'row'} gap={2}>
          <JButton
            onClick={() => navigate(Paths.EnterprisePage)}
            variant="text"
          >
            {t('CANCEL')}
          </JButton>
          {isDirty && (
            <JButton
              onClick={() => {
                if (!id || !payload) return;
                editEnterprise({ id, formData: payload });
              }}
              size="small"
              variant="contained"
              color="secondary"
            >
              {t('SAVE')}
            </JButton>
          )}
        </Stack>
      </TopBar>
      <Divider />
      <Jtab items={itemsList} />
    </>
  );
};

export default EnterpriseEditForm;
